import { useState } from "react";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import { FilterQueryInput } from "@features/metrics-explorer/components";
import { EllipsisMenu } from "@features/shared";
import { SecondaryButton } from "@shared/buttons";
import { DebouncedTextInput } from "@shared/debounced-text-input";
import { Switch } from "@shared/switch";

import { useLandingPageAnalyticsContext } from "../hooks";

import { AdvancedSearchEditor } from "./advanced-search-editor";
import { ExportTableModal } from "./export-table-modal";

const FILTER_QUERY_LABELS = {
  campaign: "CAMPAIGN SEARCH",
  adset: "CAMPAIGN/ADSET SEARCH",
  ad: "CAMPAIGN/ADSET/AD SEARCH",
} as const;

const FILTER_QUERY_PLACEHOLDERS = {
  campaign: "Search by name, campaign id...",
  adset: "Search by name, campaign id, adset id...",
  ad: "Search by name, campaign id, adset id, ad id...",
} as const;

const FILTER_QUERY_LEVELS = {
  campaign: ["campaign"],
  adset: ["campaign", "adset"],
  ad: ["campaign", "adset", "ad"],
};

export const SearchBar = () => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const {
    filters: {
      attributionModel,
      attributionWindow,
      accountingMode,
      dateRange,
      breakdownFilters,
      level,
      adQuery,
      urlQuery,
      advancedSearch,
    },
    setAdQuery,
    setUrlQuery,
    setAdvancedSearch,
    setPage,
  } = useLandingPageAnalyticsContext();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAdvancedSearch, setIsAdvancedSearch] = useState(
    advancedSearch ?? false,
  );

  const pageContext = {
    attributionModel,
    attributionWindow,
    accountingMode,
    level,
    dateRange,
    breakdownFilters,
    adQuery,
    urlQuery,
    advancedSearch,
  };

  return (
    <div className="px-6 py-4 text-xs">
      <div className="flex items-end justify-end gap-6">
        {!isAdvancedSearch && (
          <div className="flex-1 flex items-center gap-3">
            <FilterQueryInput
              className="w-full"
              dropdownClassName="z-[999]"
              label={FILTER_QUERY_LABELS[level]}
              placeholder={FILTER_QUERY_PLACEHOLDERS[level]}
              levels={FILTER_QUERY_LEVELS[level]}
              value={adQuery}
              onChange={(value) => {
                prettyLogEventPage({
                  domain: "Landing Page Analytics",
                  action: "onChange",
                  additionalActionInfo: "Set Ad Query",
                  data: {
                    newAdQuery: value,
                    pageContext,
                  },
                });

                setPage(1);
                setAdQuery(value);
              }}
            />
            <DebouncedTextInput
              label="URL SEARCH"
              placeholder="Search by URL..."
              value={urlQuery ?? ""}
              onChange={(val) => {
                const value = val === "" ? null : val;

                prettyLogEventPage({
                  domain: "Landing Page Analytics",
                  action: "onChange",
                  additionalActionInfo: "Set Url Query",
                  data: {
                    newUrlQuery: value,
                    pageContext,
                  },
                });

                setPage(1);
                setUrlQuery(value);
              }}
            />
          </div>
        )}
        <div className="flex items-center gap-2 mt-[18px]">
          <SecondaryButton
            className="font-bold flex items-center gap-2"
            onClick={() => setIsModalOpen(true)}
          >
            EXPORT TABLE
            <i className="fa-regular fa-download"></i>
          </SecondaryButton>
          <EllipsisMenu>
            <EllipsisMenu.Item
              as="button"
              className="cursor-default"
              onClick={() => {
                // Do nothing
              }}
            >
              <label className="flex items-center gap-2 m-0 cursor-pointer">
                <Switch
                  size="small"
                  value={isAdvancedSearch}
                  onChange={(value) => {
                    if (value) {
                      setAdQuery(null);
                      setUrlQuery(null);
                    } else {
                      setAdvancedSearch(null);
                    }

                    setIsAdvancedSearch(value);
                  }}
                />
                {isAdvancedSearch
                  ? "Switch to simple search"
                  : "Switch to advanced search"}
              </label>
            </EllipsisMenu.Item>
          </EllipsisMenu>
        </div>
        {isModalOpen && (
          <ExportTableModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
          />
        )}
      </div>
      {isAdvancedSearch && <AdvancedSearchEditor />}
    </div>
  );
};
